import * as React from 'react';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  DeleteButton,
  TextInput,
  NumberField,
  NumberInput,
  BooleanField,
  BooleanInput,
  DateTimeInput,
  required,
  useTranslate,
  useLocaleState,
  useRecordContext,
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  SelectArrayInput,
  FormDataConsumer,
  Toolbar,
  SaveButton,
} from 'react-admin';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import EmptyList from '../components/misc/EmptyList';
import SourceLanguageBanner from '../components/form/SourceLanguageBanner';
import { useSourceLanguage } from '../hooks/useSourceLanguage';
import { EditableImage } from '../components/form/EditableImageField';
import { resolveMediaUrl } from '../utils/mediaUrl';

const entity = 'popupAds';
const BRAND = '#AE256D';

/** Popup banner crop — genis ust alan */
const POPUP_BANNER_WIDTH = 720;
const POPUP_BANNER_HEIGHT = 200;
const POPUP_BANNER_ASPECT = POPUP_BANNER_WIDTH / POPUP_BANNER_HEIGHT;

/** Floating script/widget ikonu — kare */
const LAUNCHER_SIZE = 256;
const LAUNCHER_ASPECT = 1;

const EVENT_FILTER = {
  status: 'notPast',
  isActive: 'true',
  hasSessions: 'false',
};

const PAGE_CHOICES = [
  { id: '*', name: 'Tüm sayfalar' },
  { id: '/', name: 'Ana Sayfa (/)' },
  { id: '/etkinlikler', name: 'Etkinlikler' },
  { id: '/mekanlar', name: 'Mekanlar' },
  { id: '/haberler', name: 'Bloglar' },
  { id: '/arama', name: 'Arama' },
  { id: '/giris', name: 'Giriş' },
  { id: '/uye-ol', name: 'Üye Ol' },
];

const ColorFieldInput = ({ source, label, defaultValue = '#1a1a1a' }) => {
  const { setValue } = useFormContext();
  const value = useWatch({ name: source }) || defaultValue;
  const swatch = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : defaultValue;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 1, width: '100%' }}>
      <TextInput
        source={source}
        label={label}
        fullWidth
        defaultValue={defaultValue}
        helperText="Örn. #AE256D"
      />
      <Box
        component="input"
        type="color"
        value={swatch}
        onChange={(e) => {
          setValue(source, e.target.value, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }}
        sx={{
          width: 48,
          height: 40,
          border: '1px solid #ddd',
          borderRadius: 1,
          cursor: 'pointer',
          p: 0.25,
          mb: 2.5,
          flexShrink: 0,
        }}
      />
    </Box>
  );
};

const bannerPreviewSrc = (banner) => {
  if (!banner) return null;
  if (typeof banner === 'string') return resolveMediaUrl(banner);
  if (banner?.src) {
    if (typeof banner.src === 'string' && banner.src.startsWith('blob:')) return banner.src;
    return resolveMediaUrl(banner.src);
  }
  if (banner?.rawFile instanceof Blob) {
    try {
      return URL.createObjectURL(banner.rawFile);
    } catch {
      return null;
    }
  }
  return null;
};

const PopupAdLivePreview = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const loc = locale === 'en' ? 'en' : 'tr';

  const banner = useWatch({ name: 'banner' });
  const title = useWatch({ name: 'title' });
  const subtitle = useWatch({ name: 'subtitle' });
  const titleColor = useWatch({ name: 'titleColor' }) || '#1a1a1a';
  const subtitleColor = useWatch({ name: 'subtitleColor' }) || '#555555';
  const footerNote = useWatch({ name: 'footerNote' });
  const events = useWatch({ name: 'events' }) || [];

  const titleText =
    (typeof title === 'object' ? title?.[loc] || title?.tr || title?.en : title) ||
    translate('popupAds.preview_title_placeholder', { _: 'Başlık buraya gelecek' });
  const subtitleText =
    (typeof subtitle === 'object' ? subtitle?.[loc] || subtitle?.tr || subtitle?.en : subtitle) ||
    translate('popupAds.preview_subtitle_placeholder', { _: 'Alt başlık buraya gelecek' });
  const footerText =
    typeof footerNote === 'object'
      ? footerNote?.[loc] || footerNote?.tr || footerNote?.en
      : footerNote;
  const imgSrc = bannerPreviewSrc(banner);
  const cardCount = Array.isArray(events) ? events.filter((e) => e?.eventId).length : 0;

  return (
    <Box
      sx={{
        mt: 3,
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: '#f5f5f7',
        border: '1px solid #e8e8ec',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#333' }}>
        {translate('popupAds.preview', { _: 'Önizleme' })}
      </Typography>
      <Box
        sx={{
          maxWidth: 520,
          mx: 'auto',
          bgcolor: '#fff',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <Box
          sx={{
            height: 110,
            bgcolor: BRAND,
            backgroundImage: imgSrc ? `url(${imgSrc})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              color: '#666',
              fontWeight: 700,
            }}
          >
            ×
          </Box>
        </Box>
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 18,
              lineHeight: 1.3,
              color: titleColor,
              mb: 0.75,
            }}
          >
            {titleText}
          </Typography>
          <Typography sx={{ fontSize: 13, color: subtitleColor, lineHeight: 1.45, mb: 2 }}>
            {subtitleText}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              overflowX: 'auto',
              pb: 1,
              mx: -0.5,
              px: 0.5,
            }}
          >
            {(cardCount > 0 ? Array.from({ length: Math.min(cardCount, 4) }) : [0, 1, 2]).map(
              (_, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: '0 0 120px',
                    width: 120,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid #eee',
                    bgcolor: '#fafafa',
                  }}
                >
                  <Box sx={{ height: 72, bgcolor: '#e8e0e6' }} />
                  <Box sx={{ p: 1 }}>
                    <Box sx={{ height: 10, bgcolor: '#ddd', borderRadius: 0.5, mb: 0.5 }} />
                    <Box
                      sx={{ height: 8, width: '70%', bgcolor: '#eee', borderRadius: 0.5, mb: 0.75 }}
                    />
                    <Box
                      sx={{
                        height: 12,
                        width: '50%',
                        bgcolor: BRAND,
                        borderRadius: 0.5,
                        opacity: 0.7,
                      }}
                    />
                  </Box>
                </Box>
              ),
            )}
          </Box>
          {footerText ? (
            <Typography sx={{ mt: 1.5, fontSize: 10, color: '#999', lineHeight: 1.4 }}>
              {footerText}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Typography sx={{ mt: 1, fontSize: 11, color: '#888', textAlign: 'center' }}>
        {translate('popupAds.preview_hint', {
          _: 'Webde kartlar sizin etkinlik kart tasarımınızla slider olarak görünür.',
        })}
      </Typography>
    </Box>
  );
};

const PopupAdFormToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton alwaysEnable />
  </Toolbar>
);

const PopupAdFormFields = ({ isEdit = false }) => {
  const translate = useTranslate();
  const record = useRecordContext();
  const { localizedSource } = useSourceLanguage({ isEdit, record });

  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />

      <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
        {translate('popupAds.form_heading', { _: 'Pop-up Reklam' })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        {translate('popupAds.form_subheading', {
          _: 'Banner, başlık, etkinlikler ve hangi sayfalarda çıkacağını ayarlayın. Önizleme sağda/altta canlı güncellenir.',
        })}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
          width: '100%',
        }}
      >
        <Box>
          <TextInput
            source="name"
            label={translate('popupAds.name', { _: 'Dahili Ad' })}
            fullWidth
            validate={required()}
            helperText={translate('popupAds.name_helper', {
              _: 'Sadece panel listesinde görünür',
            })}
          />
          <NumberInput
            source="order"
            label={translate('popupAds.order', { _: 'Sıra' })}
            fullWidth
            defaultValue={0}
          />
          <BooleanInput
            source="isActive"
            label={translate('popupAds.is_active', { _: 'Aktif?' })}
            defaultValue={true}
          />
          <NumberInput
            source="dismissHours"
            label={translate('popupAds.dismiss_hours', {
              _: 'Kapatınca gizlenme (saat)',
            })}
            fullWidth
            defaultValue={24}
            helperText={translate('popupAds.dismiss_helper', {
              _: 'Kullanıcı X’e basınca bu kadar saat tekrar gösterme',
            })}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
            {translate('popupAds.countdown_heading', { _: 'Üst Bar Sayaç' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            {translate('popupAds.countdown_subheading', {
              _: 'Bitiş tarihi ve “kaç dk kala görünsün” alanını ayarlayın. Süre gelince popup floating ikonu yukarı büyür ve sayar.',
            })}
          </Typography>
          <DateTimeInput
            source="countdownEndsAt"
            label={translate('popupAds.countdown_ends_at', {
              _: 'Sayaç bitiş tarihi / saati',
            })}
            fullWidth
            helperText={translate('popupAds.countdown_ends_helper', {
              _: 'Bu anda sayaç sıfırlanır (fırsat biter)',
            })}
          />
          <NumberInput
            source="countdownShowMinutes"
            label={translate('popupAds.countdown_show_minutes', {
              _: 'Kaç dk kala sayaç çıksın',
            })}
            fullWidth
            min={0}
            helperText={translate('popupAds.countdown_show_helper', {
              _: 'Örn. 120 → bitişten 2 saat önce üst bar ve widget sayacı görünür',
            })}
          />

          <EditableImage
            source="banner"
            record={record}
            aspect={POPUP_BANNER_ASPECT}
            width={360}
            height={Math.round(360 / POPUP_BANNER_ASPECT)}
            label={translate('popupAds.banner', { _: 'Üst Banner Görseli' })}
            hint={translate('popupAds.banner_hint', {
              _: `Önerilen ölçü ${POPUP_BANNER_WIDTH}×${POPUP_BANNER_HEIGHT}. Kaydetmeden önce kırpın.`,
            })}
            cropTitle={translate('popupAds.banner_crop_title', {
              _: `Banner Kırp (${POPUP_BANNER_WIDTH}×${POPUP_BANNER_HEIGHT})`,
            })}
            cropDesc={translate('popupAds.banner_crop_desc', {
              _: 'Kırpma alanı banner oranında kilitli.',
            })}
          />

          <EditableImage
            source="launcherImage"
            record={record}
            aspect={LAUNCHER_ASPECT}
            width={140}
            height={140}
            outputMime="image/png"
            label={translate('popupAds.launcher_image', {
              _: 'Popup Floating İkon Görseli',
            })}
            hint={translate('popupAds.launcher_hint', {
              _: `Yuvarlak popup ikonu için kare görsel (${LAUNCHER_SIZE}×${LAUNCHER_SIZE}). Sayaç gelince ikon yukarı büyür ve sayar. Şeffaf PNG desteklenir.`,
            })}
            cropTitle={translate('popupAds.launcher_crop_title', {
              _: `Widget İkon Kırp (${LAUNCHER_SIZE}×${LAUNCHER_SIZE})`,
            })}
            cropDesc={translate('popupAds.launcher_crop_desc', {
              _: 'Kare kırpma — webde yuvarlak ikon olarak görünür. Şeffaflık korunur (PNG).',
            })}
          />

          <TextInput
            source={localizedSource('title')}
            label={translate('popupAds.title_field', { _: 'Başlık' })}
            fullWidth
            validate={required()}
            multiline
          />
          <ColorFieldInput
            source="titleColor"
            label={translate('popupAds.title_color', { _: 'Başlık Rengi' })}
            defaultValue="#1a1a1a"
          />

          <TextInput
            source={localizedSource('subtitle')}
            label={translate('popupAds.subtitle', { _: 'Alt Başlık' })}
            fullWidth
            multiline
          />
          <ColorFieldInput
            source="subtitleColor"
            label={translate('popupAds.subtitle_color', { _: 'Alt Başlık Rengi' })}
            defaultValue="#555555"
          />

          <TextInput
            source={localizedSource('footerNote')}
            label={translate('popupAds.footer_note', { _: 'Alt Not (ince yazı)' })}
            fullWidth
            multiline
            rows={2}
          />

          <SelectArrayInput
            source="pages"
            label={translate('popupAds.pages', { _: 'Gösterilecek Sayfalar' })}
            choices={PAGE_CHOICES}
            fullWidth
            defaultValue={['/']}
            validate={required()}
            helperText={translate('popupAds.pages_helper', {
              _: '“Tüm sayfalar” seçilirse site genelinde çıkar',
            })}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
            {translate('popupAds.events', { _: 'Etkinlikler (Slider)' })}
          </Typography>
          <ArrayInput source="events" label="" defaultValue={[]}>
            <SimpleFormIterator inline={false}>
              <ReferenceInput
                source="eventId"
                reference="events-v2"
                filter={EVENT_FILTER}
                label={translate('popupAds.event', { _: 'Etkinlik' })}
                sort={{ field: 'startdate', order: 'ASC' }}
                perPage={50}
              >
                <AutocompleteInput
                  optionText={(r) => `${r.name} - ${moment(r.startdate).format('DD-MM-YYYY')}`}
                  matchSuggestion={() => true}
                  filterToQuery={(searchText) => ({ q: searchText })}
                  fullWidth
                />
              </ReferenceInput>
            </SimpleFormIterator>
          </ArrayInput>
        </Box>

        <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
          <FormDataConsumer>{() => <PopupAdLivePreview />}</FormDataConsumer>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const PopupAdList = (props) => {
  const translate = useTranslate();
  return (
    <List
      {...props}
      title={translate('popupAds.title', { _: 'Pop-up Reklamlar' })}
      empty={<EmptyList entity={entity} />}
      sort={{ field: 'order', order: 'ASC' }}
      exporter={false}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label={translate('popupAds.name', { _: 'Ad' })} />
        <NumberField source="order" label={translate('popupAds.order', { _: 'Sıra' })} />
        <BooleanField source="isActive" label={translate('popupAds.is_active', { _: 'Aktif?' })} />
        <EditButton />
        <DeleteButton basePath={`/${entity}`} />
      </Datagrid>
    </List>
  );
};

export const PopupAdEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('popupAds.edit_title', { _: 'Pop-up Düzenle' })}
      {...props}
      mutationMode="pessimistic"
    >
      <SimpleForm redirect={`/${entity}`} toolbar={<PopupAdFormToolbar />}>
        <PopupAdFormFields isEdit />
      </SimpleForm>
    </Edit>
  );
};

export const PopupAdCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create title={translate('popupAds.create_title', { _: 'Yeni Pop-up' })} {...props}>
      <SimpleForm
        redirect={`/${entity}`}
        toolbar={<PopupAdFormToolbar />}
        defaultValues={{
          isActive: true,
          order: 0,
          dismissHours: 24,
          pages: ['/'],
          events: [],
          titleColor: '#1a1a1a',
          subtitleColor: '#555555',
        }}
      >
        <PopupAdFormFields />
      </SimpleForm>
    </Create>
  );
};
